import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Copy,
  Pencil,
  Plus,
  Printer,
  Ruler,
  Stamp,
  Star,
  Trash2,
  Upload,
} from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import {
  LabelLayoutSchema,
  type LabelTemplateSummaryType,
} from '@aeris/contracts'
import {
  isRollLayout,
  renderCalibrationHtml,
  renderLayoutCalibrationHtml,
  renderLayoutSheetHtml,
  WARRANTY_SEAL_NAME,
  warrantySealLayout,
} from '@aeris/printing'
import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  QueryState,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { RollTestDialog } from '../components/roll-test-dialog.tsx'
import {
  createLabelTemplate,
  deleteLabelTemplate,
  fetchLabelTemplates,
  fileToBase64,
  importLabelTemplate,
  updateLabelTemplate,
} from '../lib/api/labels.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import { DEFAULT_LABEL_TEMPLATE } from '../lib/label-template.ts'
import { printedOnToday } from '../lib/printed-on.ts'

/**
 * Os modelos de etiqueta da loja — F124.
 *
 * ## O que esta tela deixou de ser
 *
 * Ela nasceu configurando o adesivo **deste computador**, em `localStorage`,
 * por decisão do dono. Em 12/09 ele mandou os seis `.conf` que a Elite Digital
 * usa no GDOOR e pediu o contrário: modelo no banco, importado desses arquivos.
 *
 * A razão é concreta. Aqueles modelos foram desenhados anos atrás e estão
 * conferidos contra papel de verdade; por computador significaria reconfigurar
 * cada máquina do balcão, e perder tudo quando alguém trocasse de navegador. Um
 * modelo de etiqueta é vocabulário da loja, como a tabela de preço.
 */

const PREVIEW = {
  name: 'Bateria iPhone 11 Original',
  sku: '000123',
  gtin: null,
  salePrice: 'R$ 189,90',
  salePriceAmount: '189.900000',
  lastPurchaseAt: '01/09/2026',
  brand: 'Apple',
  unit: 'UN',
  supplierReference: 'WEFIX',
  location: 'Gaveta 3',
}

const TemplatePreview = ({
  template,
}: {
  template: LabelTemplateSummaryType
}): ReactNode => (
  <iframe
    title={`Pré-visualização de ${template.name}`}
    srcDoc={renderLayoutSheetHtml(template.layout, [PREVIEW], {
      printedOn: printedOnToday(),
    })}
    /*
     * Altura calculada do adesivo, e não fixa.
     *
     * Com altura fixa, os modelos de 75 e 98 mm apareciam cortados na prévia e
     * pareciam errados sem estar — o que faz alguém "consertar" um modelo que
     * imprime certo. 96 px por polegada é o que o CSS usa para `mm`.
     */
    style={{
      height: `${String(
        Math.ceil(
          ((template.layout.page.marginTopMm +
            template.layout.label.heightMm +
            4) *
            96) /
            25.4
        )
      )}px`,
    }}
    className="w-full rounded-(--radius-md) border border-(--border-default) bg-white"
  />
)

export const ModeloDeEtiquetaPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const { allows, session } = useAuth()
  const canWrite = allows('catalog.write')

  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [rollTest, setRollTest] = useState<LabelTemplateSummaryType | null>(
    null
  )

  const templates = useQuery({
    queryKey: ['label-templates'],
    queryFn: async ({ signal }) => await fetchLabelTemplates('active', signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['label-templates'] })
  }

  const report = (cause: unknown, fallback: string): void => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const importing = useMutation({
    mutationFn: async (files: readonly File[]) => {
      /*
       * Um por vez, e não em paralelo.
       *
       * O primeiro modelo importado vira o padrão da loja, e isso é decidido no
       * servidor contando quantos já existem. Seis requisições ao mesmo tempo
       * contariam zero seis vezes, e o índice único de padrão recusaria cinco
       * delas — com o lojista vendo cinco erros e nenhuma explicação.
       */
      for (const file of files) {
        await importLabelTemplate({
          fileName: file.name,
          contentBase64: await fileToBase64(file),
        })
      }
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      report(cause, 'Não foi possível importar o arquivo.')
    },
  })

  const promote = useMutation({
    mutationFn: async (template: LabelTemplateSummaryType) =>
      await updateLabelTemplate(template.id, {
        version: template.version,
        isDefault: true,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      report(cause, 'Não foi possível marcar como padrão.')
    },
  })

  /*
   * Duplicar para editar sem mexer no que está imprimindo (F129). A cópia é
   * um modelo do Aeris, com o layout inteiro do original.
   */
  const duplicate = useMutation({
    mutationFn: async (template: LabelTemplateSummaryType) =>
      await createLabelTemplate({
        name: `${template.name} (cópia)`.slice(0, 120),
        layout: template.layout,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      report(cause, 'Não foi possível duplicar o modelo.')
    },
  })

  /*
   * O selo de garantia 4×10 pronto (F140), igual à folha que sai do GDOOR, e já
   * como padrão: é o modelo que a fila de etiquetas abre escolhido. O nome da
   * loja em vermelho vem da empresa da sessão.
   */
  const createSeal = useMutation({
    mutationFn: async () => {
      const created = await createLabelTemplate({
        name: WARRANTY_SEAL_NAME,
        // Pelo mesmo schema do servidor: confere e devolve o tipo da fiação.
        layout: LabelLayoutSchema.parse(
          warrantySealLayout(session?.context?.companyName ?? '')
        ),
      })
      if (!created.isDefault) {
        await updateLabelTemplate(created.id, {
          version: created.version,
          isDefault: true,
        })
      }
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.status === 409) {
        setError(
          `O modelo "${WARRANTY_SEAL_NAME}" já existe na lista. Use Editar para ajustá-lo, ou Padrão para a fila abrir com ele.`
        )
        return
      }
      report(cause, 'Não foi possível criar o selo de garantia.')
    },
  })

  const remove = useMutation({
    mutationFn: async (template: LabelTemplateSummaryType) => {
      await deleteLabelTemplate(template.id)
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      report(cause, 'Não foi possível apagar o modelo.')
    },
  })

  const print = (html: string): void => {
    setError(null)
    printHtmlDocument(html).catch((cause: unknown) => {
      report(cause, 'Não foi possível abrir a impressão.')
    })
  }

  const rows = templates.data ?? []

  return (
    <>
      {/*
        As ações no cabeçalho da aba, como em toda aba (F132). Antes elas
        moravam num bloco próprio abaixo dele — a única aba assim.
      */}
      <TabHeader
        title="Modelos de etiqueta"
        description={
          <>
            Os modelos que a loja usa para imprimir etiqueta de produto. Crie um
            do zero, <strong>edite as medidas e os campos</strong> de qualquer
            modelo, ou importe os arquivos .conf do GDOOR.
          </>
        }
        actions={
          canWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Ruler aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  print(renderCalibrationHtml(DEFAULT_LABEL_TEMPLATE))
                }}
              >
                Etiqueta de teste
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={createSeal.isPending}
                leadingIcon={<Stamp aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  createSeal.mutate()
                }}
              >
                {createSeal.isPending
                  ? 'Criando…'
                  : 'Criar selo de garantia 4×10'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={importing.isPending}
                leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  fileInput.current?.click()
                }}
              >
                {importing.isPending ? 'Importando…' : 'Importar do GDOOR'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                asChild
              >
                <Link to="/modelo-de-etiqueta/$id" params={{ id: 'novo' }}>
                  Novo modelo
                </Link>
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        {canWrite ? (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept=".conf,.txt,.conf.txt"
              className="sr-only"
              onChange={(event) => {
                const files = [...(event.target.files ?? [])]
                // Limpa antes de disparar: sem isto, escolher o MESMO arquivo
                // outra vez não dispara evento nenhum e a tela parece travada.
                event.target.value = ''
                if (files.length > 0) importing.mutate(files)
              }}
            />
            <p className="text-(length:--text-xs) text-(--text-muted)">
              Pode importar vários arquivos de uma vez. A etiqueta de teste traz
              uma régua de 10&nbsp;mm:{' '}
              <strong>meça com uma régua de verdade</strong>. Se der 10&nbsp;mm,
              o computador e a impressora concordam sobre o que é um milímetro.
              Num modelo de folha, a <strong>Folha de teste</strong> desenha o
              contorno de cada adesivo: imprima em papel comum, ponha sobre a
              folha de adesivos contra a luz e confira se cai nos recortes. Num
              modelo de bobina, o <strong>Teste da bobina</strong> imprime duas
              fileiras no próprio adesivo e diz qual papel criar no driver da
              impressora. Para imprimir produto,{' '}
              <Link
                to="/etiquetas"
                className="text-(--brand-primary) underline-offset-4 hover:underline"
              >
                vá para a fila de etiquetas
              </Link>
              .
            </p>
          </>
        ) : null}

        {error === null ? null : (
          <InlineAlert tone="danger">{error}</InlineAlert>
        )}

        {/* Carregando, não carregou, vazio e a lista — o erro vem antes do
            vazio: uma consulta que falha não pode afirmar que a loja não tem
            modelo nenhum (quem lê isso importa tudo de novo). */}
        <QueryState
          isPending={templates.isPending}
          isError={templates.isError}
          error={templates.error}
          onRetry={() => {
            void templates.refetch()
          }}
          what="os modelos"
          isEmpty={rows.length === 0}
          empty={
            <EmptyState
              icon={<Printer aria-hidden="true" className="h-8 w-8" />}
              title="Nenhum modelo ainda"
              description={
                canWrite
                  ? 'Crie um em "Novo modelo" ou traga os do GDOOR em "Importar do GDOOR". Sem modelo, a etiqueta sai no formato simples do Aeris.'
                  : 'Quem administra o catálogo ainda não criou nenhum.'
              }
            />
          }
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Modelo</TableHeaderCell>
                  <TableHeaderCell>Adesivo</TableHeaderCell>
                  <TableHeaderCell>Folha</TableHeaderCell>
                  {canWrite ? (
                    <TableHeaderCell className="text-right">
                      Ações
                    </TableHeaderCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-2">
                          {template.name}
                          {template.isDefault ? (
                            <Badge tone="success">padrão</Badge>
                          ) : null}
                        </span>
                        <span className="text-(length:--text-xs) text-(--text-muted)">
                          {template.sourceFile ?? 'feito no Aeris'} ·{' '}
                          {template.layout.elements.length} campos
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {template.layout.label.widthMm} ×{' '}
                      {template.layout.label.heightMm} mm
                    </TableCell>
                    <TableCell>
                      {template.layout.columns} × {template.layout.rows} por
                      folha
                    </TableCell>
                    {canWrite ? (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPreview(
                                preview === template.id ? null : template.id
                              )
                            }}
                          >
                            {preview === template.id ? 'Fechar' : 'Ver'}
                          </Button>
                          {template.layout.rows > 1 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Folha de teste de ${template.name}`}
                              leadingIcon={
                                <Ruler
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                              onClick={() => {
                                print(
                                  renderLayoutCalibrationHtml(template.layout)
                                )
                              }}
                            >
                              Folha de teste
                            </Button>
                          ) : isRollLayout(template.layout) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Teste da bobina de ${template.name}`}
                              leadingIcon={
                                <Ruler
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                              onClick={() => {
                                setRollTest(template)
                              }}
                            >
                              Teste da bobina
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={
                              <Pencil
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                            asChild
                          >
                            <Link
                              to="/modelo-de-etiqueta/$id"
                              params={{ id: template.id }}
                            >
                              Editar
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={duplicate.isPending}
                            leadingIcon={
                              <Copy
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                            onClick={() => {
                              duplicate.mutate(template)
                            }}
                          >
                            Duplicar
                          </Button>
                          {template.isDefault ? null : (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={promote.isPending}
                              leadingIcon={
                                <Star
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                              onClick={() => {
                                promote.mutate(template)
                              }}
                            >
                              Padrão
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={remove.isPending}
                            aria-label={`Apagar o modelo ${template.name}`}
                            leadingIcon={
                              <Trash2
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                            onClick={() => {
                              remove.mutate(template)
                            }}
                          >
                            Apagar
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </QueryState>

        {rows
          .filter((template) => template.id === preview)
          .map((template) => (
            <section key={template.id} className="flex flex-col gap-2">
              <h3 className="text-(length:--text-sm) font-medium">
                {template.name}
              </h3>
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Um adesivo preenchido com um produto de exemplo — desenhado pelo
                mesmo código que imprime.
              </p>
              <TemplatePreview template={template} />
            </section>
          ))}
      </div>

      {rollTest === null ? null : (
        <RollTestDialog
          layout={rollTest.layout}
          onClose={() => {
            setRollTest(null)
          }}
        />
      )}
    </>
  )
}
